import React, { useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import CommentItem from './CommentItem';
import { createClient } from '@/lib/supabase/component';
import CommentForm from './CommentForm';
import { useCommentsStore } from '@/lib/stores/post';
import { useAuth } from '@/components/Auth/AuthProvider';
import Loading from '@/components/Loading/Loading';
import { cn } from '@/lib/utils/helpers';
import { TooltipProvider } from '@/lib/shadcn/ui/tooltip';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/lib/shadcn/ui/alert-dialog"

/**
 * CommentList component for displaying a list of comments
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes to apply
 * @param {number} props.storyId - ID of the story/post to show comments for
 * @returns {JSX.Element} CommentList component
 */
const supabase = createClient();

const CommentList = memo(function ({ className, storyId }) {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [commentToDelete, setCommentToDelete] = React.useState(null);

  const {
    fetchComments,
    getComments,
    isLoading,
    getError,
    getTotalCount,
    getVisibleCount,
    loadMoreComments,
    canLoadMore,
    replyingTo,
    setReplyingTo,
    deleteComment
  } = useCommentsStore(state => ({
    fetchComments: state.fetchComments,
    getComments: state.getComments,
    isLoading: state.isLoading,
    getError: state.getError,
    getTotalCount: state.getTotalCount,
    getVisibleCount: state.getVisibleCount,
    loadMoreComments: state.loadMoreComments,
    canLoadMore: state.canLoadMore,
    replyingTo: state.replyingTo,
    setReplyingTo: state.setReplyingTo,
    deleteComment: state.deleteComment
  }));

  // Load comments when the component mounts or storyId changes
  useEffect(() => {
    if (storyId) {
      fetchComments(supabase, storyId);
    }

    // Set up real-time subscription for new comments
    /* const subscription = supabase
      .channel('comments-channel')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'comments', filter: `story_id=eq.${storyId}` },
        (payload) => {
          // Refresh comments when there's a change
          fetchComments(supabase, storyId);
        }
      )
      .subscribe(); */

    // Clean up subscription on unmount
    return () => {
      // subscription.unsubscribe();
    };
  }, [storyId]);

  const handleLoadMore = () => {
    loadMoreComments(storyId);
    // After updating the visible count, fetch more comments
    fetchComments(supabase, storyId);
  };

  const handleReply = (commentId) => {
    setReplyingTo(commentId === replyingTo ? null : commentId);
  };

  const handleDelete = (commentId) => {
    setCommentToDelete(commentId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (commentToDelete) {
      deleteComment(supabase, commentToDelete, storyId);
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCommentToDelete(null);
  };

  const comments = getComments(storyId);
  const loading = isLoading(storyId);
  const error = getError(storyId);
  const totalCount = getTotalCount(storyId);
  const visibleCount = getVisibleCount(storyId);
  const { user } = useAuth() || {};

  if (loading && comments.length === 0) {
    return <Loading className="min-h-[100px]" />;
  }

  if (error) {
    return <div className="text-center py-4 text-red-500">{error}</div>;
  }

  if (comments.length === 0) {
    return <div className="text-center py-4">No comments yet. Be the first to give your opinion!</div>;
  }

  return (
    <TooltipProvider>
      <div className={cn('w-full', className)}>
        {comments.map((comment) => (
        <React.Fragment key={comment.id}>
          <CommentItem
            user={user}
            id={comment.id}
            author={comment.profiles_public?.full_name || comment.profiles?.username || 'Anonymous'}
            authorAvatar={comment.profiles_public?.avatar_url}
            content={comment.content}
            createdAt={comment.created_at}
            onReply={() => handleReply(comment.id)}
            onDelete={() => handleDelete(comment.id)}
            status={comment.status}
            ownerId={comment.user_id}
          />

          {replyingTo === comment.id && (
            <div className="ml-4 mb-6">
              <CommentForm
                storyId={storyId}
                replyToId={comment.id}
                className="mt-2"
              />
            </div>
          )}

          {/* Display replies */}
          {comment.replies && comment.replies.map((reply, index) => (
            <CommentItem
              key={reply.id}
              user={user}
              id={reply.id}
              author={reply.profiles_public?.full_name || reply.profiles?.username || 'Anonymous'}
              authorAvatar={reply.profiles_public?.avatar_url}
              content={reply.content}
              createdAt={reply.created_at}
              isReply={true}
              ownerId={reply.user_id}
              onDelete={() => handleDelete(reply.id)}
              status={reply.status}
              className={index === comment.replies.length - 1 ? 'rounded-b-xl' : 'mb-0 pb-8'}
            />
          ))}
        </React.Fragment>
      ))}

      {canLoadMore(storyId) && (
        <div className="flex justify-center items-center my-2 lg:my-6">
          <div className="flex-grow h-px bg-navy dark:bg-silver"></div>
          <button
            onClick={handleLoadMore}
            className="mx-3 px-4 py-0.5 text-sm font-medium border border-navy dark:border-silver hover:bg-navy hover:text-white dark:hover:bg-silver dark:hover:text-white"
          >
            Load more comments ({totalCount - visibleCount} remaining)
          </button>
          <div className="flex-grow h-px bg-navy dark:bg-silver"></div>
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="mb-0">Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the comment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </TooltipProvider>
  );
});

export default CommentList;

CommentList.propTypes = {
  className: PropTypes.string,
  storyId: PropTypes.number.isRequired,
};
